// Nieuwe taak (Taak-hub), met de markup van het prototype (NEWTASK in design/build/prototype.js).
// Klant, bucket, titel, start en deadline, de schatting, de eigenaar, eventueel de fase waaronder de taak komt (een
// subtaak in Planner, in de bucket van die fase) en eventueel een fase waarop ze wacht. De taak komt meteen op het bord;
// Planner verwerkt ze in 1 à 2 minuten. Een medewerker maakt een taak voor zichzelf.
// Met Clockify: met een schatting maakt de server ook de Clockify-taak (in het project van het klantplan), en de
// eigenaars staan op vrije uren in de gekozen periode, de meeste eerst. Lukt het Clockify-deel niet, dan staat de taak
// toch in Planner en toont de melding waarom. Zonder Clockify (of voor wie er niet in staat): de minst belaste collega's
// bovenaan, op basis van hun actieve taken.
import { useQueryClient } from "@tanstack/react-query";
import { useMemo, useState } from "react";
import { freeHours, useClockifyLive } from "../lib/api-clockify";
import { LOAD_OPTS, mergeAbsences, spanText, useCapacityView } from "../lib/api-teams";
import { catOf, fridays } from "../lib/board";
import { faseChain, padText } from "../lib/fases";
import { useCreateTask, useIndex, useMe, usePlanning } from "../lib/hooks";
import { Icon } from "../lib/icons";
import { addDays, ddmm, deadline, firstName } from "../lib/planning";
import { rightsOf, useSettings } from "../lib/settings";
import type { Fase, Person } from "../lib/types";
import { useAppState } from "../shell/AppState";
import { DateField } from "./DateField";
import { Av } from "./Avatar";

// genoeg ruimte zonder schatting: twee werkdagen, zoals het voorbeeld in het prototype
const ENOUGH = 16;

interface Cand {
  p: Person;
  /** actieve taken, waarvan over deadline (zonder capaciteit) */
  n: number;
  late: number;
  /** vrije uren in de periode en de gemiddelde bezetting; null zonder capaciteit voor deze persoon */
  free: number | null;
  avg: number | null;
  note: string;
}

export function NewTaskDrawer() {
  const { data } = usePlanning();
  const { data: me } = useMe();
  const { data: st } = useSettings();
  const { klant: klantFilter, setNewTaskOpen } = useAppState();
  const create = useCreateTask();
  const qc = useQueryClient();
  const med = me?.role === "medewerker";
  const cv = useCapacityView(!!me && (!med || rightsOf(st?.settings).seeCapacity));
  const { data: live } = useClockifyLive();
  const lv = live && live.clockify.state !== "uit" ? live : null;
  const klanten = useMemo(() => [...(data?.klanten || [])].sort((a, b) => a.name.localeCompare(b.name)), [data]);
  const [pid, setPid] = useState(() => (klantFilter && klanten.some((k) => k.id === klantFilter) ? klantFilter : klanten[0]?.id || ""));
  const buckets = useMemo(() => (data?.buckets || []).filter((b) => b.projectId === pid).sort((a, b) => a.name.localeCompare(b.name)), [data, pid]);
  const fases = useMemo(() => (data?.fases || []).filter((f) => f.projectId === pid && f.progress < 1).sort((a, b) => a.start.localeCompare(b.start)), [data, pid]);
  // dezelfde open fases als boom in de volgorde van Planner, om te kiezen waaronder de taak komt
  const tree = useMemo(() => {
    const ids = new Set(fases.map((f) => f.id)), out: { f: Fase; lvl: number }[] = [];
    const walk = (pid: string | null, lvl: number, seen: Set<string>) => {
      for (const f of fases.filter((x) => (pid ? x.parentId === pid : !x.parentId || !ids.has(x.parentId))).sort((a, b) => a.order - b.order || a.title.localeCompare(b.title))) {
        if (seen.has(f.id)) continue;
        out.push({ f, lvl });
        walk(f.id, lvl + 1, new Set([...seen, f.id]));
      }
    };
    walk(null, 0, new Set());
    return out;
  }, [fases]);
  const idx = useIndex(data);
  const [bucket, setBucket] = useState("");
  const [title, setTitle] = useState("");
  const [start, setStart] = useState(data?.today || "");
  const [end, setEnd] = useState(data ? fridays(data.today)[1] : "");
  const [est, setEst] = useState("");
  const [fase, setFase] = useState<string | null>(null);
  const [parent, setParent] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // de schatting gaat naar Clockify; zonder Clockify heeft ze geen plaats
  const clockOn = !!lv || !!cv;
  const estN = est.trim() === "" ? null : Number(est.replace(",", "."));
  const estOk = estN == null || (Number.isFinite(estN) && estN >= 0 && estN <= 2000);

  // de periode waarin we vrije uren tellen: van de start (niet vóór vandaag) tot de deadline, binnen de horizon
  const win = useMemo(() => {
    if (!cv || !data || !start || !end || end < start) return null;
    const last = addDays(cv.cap.weeks[cv.cap.weeks.length - 1].monday, 6);
    const from = start > data.today ? start : data.today, to = end < last ? end : last;
    return from <= to ? { from, to } : null;
  }, [cv, data, start, end]);

  // kandidaten: met capaciteit de meeste vrije uren eerst, anders de minst belaste (actieve taken); een medewerker alleen zichzelf
  const cands = useMemo<Cand[]>(() => {
    if (!data || !me) return [];
    const klant = data.klanten.find((k) => k.id === pid);
    const people = data.people.filter((p) => !med || p.id === me.id);
    const out = people.map((p): Cand => {
      const mine = data.tasks.filter((t) => catOf(t) === "actief" && t.owners.some((o) => o.personId === p.id));
      const n = mine.length, late = mine.filter((t) => t.end < data.today).length;
      const cp = cv?.people.get(p.id);
      if (!cv || !cp || !win) {
        return { p, n, late, free: null, avg: null, note: (n === 1 ? "1 actieve taak" : n + " actieve taken") + (late ? " · " + late + " over deadline" : "") + (cv && !cp ? " · niet in Clockify" : "") };
      }
      const free = freeHours(cp, cv.cap.weeks, win.from, win.to, LOAD_OPTS);
      const pcts = cv.cap.weeks.flatMap((w, i) => (w.monday <= win.to && addDays(w.monday, 6) >= win.from ? [cv.load(cp, i).pct] : [])).filter((x) => x != null);
      const avg = pcts.length ? Math.round(pcts.reduce((a, x) => a + x, 0) / pcts.length) : null;
      const leave = mergeAbsences(cp.weeks.flatMap((w) => w.absent).filter((a) => a.kind === "verlof" && a.from <= win.to && a.to >= win.from));
      const forKlant = klant ? mine.filter((t) => t.projectId === klant.id).length : 0;
      const busy = avg != null ? "gemiddeld " + avg + " % bezet" + (med ? " in deze periode" : "") : "";
      const note = med
        ? ["U wordt de eigenaar", leave.length ? "verlof " + leave.map(spanText).join(", ") : "", busy]
        : [leave.length ? "verlof " + leave.map(spanText).join(", ") : "", forKlant ? "werkt al aan " + (forKlant === 1 ? "1 taak" : forKlant + " taken") + " voor " + klant!.name : "",
          avg != null && avg > cv.over ? "overvol in deze periode" : "", busy];
      return { p, n, late, free, avg, note: note.filter(Boolean).join(" · ") };
    });
    return out.sort((a, b) => (a.free == null ? 1 : 0) - (b.free == null ? 1 : 0) || (b.free ?? 0) - (a.free ?? 0) || a.n - b.n || a.p.name.localeCompare(b.p.name));
  }, [data, me, med, cv, win, pid]);
  const byFree = cands.some((c) => c.free != null);
  // undefined: nog niets gekozen (de eerste kandidaat), null: zonder eigenaar
  const [owner, setOwner] = useState<string | null | undefined>(undefined);
  const chosen = med ? me?.id ?? null : owner === undefined ? cands[0]?.p.id ?? null : owner;

  if (!data || !me) return null;
  const close = () => setNewTaskOpen(false);
  // onder een fase: de bucket van die fase; wachten op die fase (of een fase erboven) kan dan niet
  const chain = faseChain(parent, idx.fases), above = new Set(chain.map((f) => f.id));
  const parentBucket = parent ? buckets.find((b) => b.id === idx.fases.get(parent)?.bucketId) : undefined;
  const bucketId = parentBucket ? parentBucket.id : buckets.some((b) => b.id === bucket) ? bucket : buckets[0]?.id || "";
  const pickParent = (id: string | null) => { setParent(id); if (id && fase && faseChain(id, idx.fases).some((f) => f.id === fase)) setFase(null); };
  const klantName = data.klanten.find((k) => k.id === pid)?.name || "";
  // het Clockify-project van dit klantplan; zonder koppeling komt de schatting er (nog) niet
  const plan = lv ? lv.plans[pid] || null : null;
  const need = estN && estOk ? estN : ENOUGH;
  const freeCls = (c: Cand) => (c.avg != null && c.avg > (cv?.over ?? 100) ? "t-err" : (c.free ?? 0) >= need ? "t-ok" : "t-warn");
  const ownerTitle = win && byFree
    ? (med ? "Eigenaar · uw ruimte van " : "Eigenaar · wie heeft ruimte van ") + ddmm(win.from) + " tot " + ddmm(win.to) + (med ? "" : "?")
    : med ? "Eigenaar · u" : "Eigenaar · de minst belaste collega's bovenaan";

  const submit = async () => {
    const t = title.trim();
    if (!t) { setError("Geef de taak een titel."); return; }
    if (!start || !end) { setError("Kies een start en een deadline."); return; }
    if (end < start) { setError("De deadline ligt vóór de start."); return; }
    if (!bucketId) { setError("Deze klant heeft nog geen bucket in Planner."); return; }
    if (clockOn && !estOk) { setError("De schatting is een aantal uren tussen 0 en 2000."); return; }
    const who = chosen ? data.people.find((p) => p.id === chosen) : null;
    const estimate = clockOn && estN ? estN : null;
    try {
      await create.run(
        { projectId: pid, bucketId, title: t, start, end, ownerId: chosen, faseId: fase, parentId: parent, estimate },
        "Aangemaakt in Planner" + (estimate && plan ? " en Clockify" : "") + ": " + t + (parent ? " onder " + (idx.fases.get(parent)?.title || "de fase") : "") + (who && !med ? ", voor " + who.first : "") + ", deadline " + ddmm(end),
      );
      // de schatting telt meteen mee in de bezetting en de uren
      if (clockOn) for (const k of ["capacity", "clockify-live"]) qc.invalidateQueries({ queryKey: [k] });
      close();
    } catch { /* de melding toont de fout */ }
  };

  // dezelfde kalender als in de taaklade: een start na de deadline neemt de deadline mee, een deadline voor de start de start
  const pickStart = (s: string) => { setStart(s); if (end && s > end) setEnd(s); setError(null); };
  const pickEnd = (e: string) => { setEnd(e); if (start && e < start) setStart(e); setError(null); };
  const dates = (
    <>
      <DateField label="Start (vanaf wanneer)" value={start} today={data.today} other={end || undefined} otherLabel="Deadline" onPick={pickStart} />
      <DateField label="Deadline" value={end} today={data.today} other={start || undefined} otherLabel="Start" align="right" onPick={pickEnd} />
    </>
  );

  return (
    <>
      <button type="button" className="p-scrim" aria-label="Sluiten" onClick={close} />
      <aside className="p-drawer" role="dialog" aria-modal="true" aria-labelledby="nt">
        <div className="p-drawer-h">
          <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12 }}>
            <div>
              <p className="ex-eyebrow" style={{ margin: 0 }}>Taak-hub</p>
              <h2 id="nt">Nieuwe taak</h2>
              <p style={{ margin: "4px 0 0", fontSize: 14, color: "var(--fg-2)" }}>{clockOn
                ? "Wordt aangemaakt in Planner, met een schatting ook in Clockify; de schatting telt meteen mee in de bezetting."
                : "Wordt aangemaakt in Planner en staat meteen op het bord; Planner verwerkt ze in 1 à 2 minuten."}</p>
            </div>
            <button type="button" className="ex-icon-btn ex-icon-btn--square" aria-label="Sluiten" title="Sluiten" onClick={close}><Icon name="x" size={18} /></button>
          </div>
        </div>

        <div className="p-drawer-b" style={{ overflowY: "auto" }}>
          <div className="p-grid2">
            <label className="ex-field"><span className="ex-label">Klant (plan)</span>
              <select className="ex-input" aria-label="Klant" value={pid} onChange={(e) => { setPid(e.target.value); setBucket(""); setFase(null); setParent(null); }}>
                {klanten.map((k) => <option key={k.id} value={k.id}>{k.name}</option>)}
              </select>
            </label>
            <label className="ex-field"><span className="ex-label">Bucket</span>
              <select className="ex-input" aria-label="Bucket" value={bucketId} disabled={!!parentBucket} title={parentBucket ? "Onder een fase: de bucket van die fase" : undefined} onChange={(e) => setBucket(e.target.value)}>
                {buckets.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
              </select>
            </label>
          </div>
          <label className="ex-field"><span className="ex-label">Titel</span>
            <input className="ex-input" type="text" autoFocus placeholder="Bijvoorbeeld: Coolblue to TAS" value={title} maxLength={250} onChange={(e) => { setTitle(e.target.value); setError(null); }} onKeyDown={(e) => { if (e.key === "Enter") submit(); }} />
          </label>
          {error && <div className="ex-notice ex-notice--warning p-notice"><Icon name="alert" size={16} /><p>{error}</p></div>}
          {clockOn ? (
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 110px", gap: 12 }}>
              {dates}
              <label className="ex-field"><span className="ex-label">Schatting (u)</span>
                <input className="ex-input" type="number" min={1} step={1} placeholder="16" aria-label="Schatting in uren (optioneel)" value={est} onChange={(e) => { setEst(e.target.value); setError(null); }} />
              </label>
            </div>
          ) : <div className="p-grid2">{dates}</div>}
          {lv && !plan && estN ? <p className="p-kd-note" style={{ margin: 0 }}>Dit klantplan hangt nog niet aan een Clockify-project: de taak komt in Planner, de schatting nog niet. Klantplannen koppelt de planner in Koppelingen.</p> : null}

          <div className="p-fs"><h3>{ownerTitle}</h3>
            <div style={{ display: "flex", flexDirection: "column", gap: 8, maxHeight: 340, overflowY: "auto" }}>
              {cands.map((c) => (
                <button key={c.p.id} type="button" className={"p-cand" + (chosen === c.p.id ? " on" : "")} onClick={() => setOwner(c.p.id)}>
                  <span className={"p-radio" + (chosen === c.p.id ? " on" : "")} /><Av id={c.p.id} init={c.p.initials} />
                  <span><b>{c.p.name}{c.p.id === me.id && !med ? " (u)" : ""}</b><span className="m">{c.note}</span></span>
                  {c.free != null ? <span className={freeCls(c)} style={{ fontSize: 14, fontWeight: 700, whiteSpace: "nowrap" }}>{Math.round(c.free)} u vrij</span> : <span />}
                </button>
              ))}
              {!med && (
                <button type="button" className={"p-cand" + (chosen === null ? " on" : "")} onClick={() => setOwner(null)}>
                  <span className={"p-radio" + (chosen === null ? " on" : "")} /><span className="p-av none"><Icon name="userx" size={13} /></span>
                  <span><b>Zonder eigenaar</b><span className="m">Komt in de kolom Zonder eigenaar; een collega pakt ze op</span></span><span />
                </button>
              )}
            </div>
            {win && byFree && end > win.to && <p className="p-kd-note">Vrije uren tot {ddmm(win.to)}: verder kijkt de capaciteit niet vooruit.</p>}
          </div>

          {tree.length > 0 && (
            <div className="p-fs"><h3>Onder fase</h3>
              <div className="p-ntf">
                <button type="button" className={"p-tog" + (!parent ? " on" : "")} aria-pressed={!parent} onClick={() => pickParent(null)}>Geen fase</button>
                {tree.map(({ f, lvl }) => (
                  <button key={f.id} type="button" className={"p-tog" + (parent === f.id ? " on" : "")} style={{ marginLeft: Math.min(lvl, 4) * 18 }} aria-pressed={parent === f.id} onClick={() => pickParent(parent === f.id ? null : f.id)}>
                    <Icon name="fase" size={13} />{f.title}
                    <span className="t-mute" style={{ fontWeight: 400 }}>· {deadline(f.end, data.today).text}</span>
                  </button>
                ))}
              </div>
              <p className="p-kd-note">{parent
                ? "De taak komt in Planner onder " + padText(chain) + (parentBucket ? ", in de bucket " + parentBucket.name + " van die fase." : ".")
                : "Kies een fase om de taak er als subtaak onder te zetten, zoals in Planner."}</p>
            </div>
          )}

          {fases.some((f) => !above.has(f.id)) && (
            <div className="p-fs"><h3>Wacht op</h3>
              <div className="p-quick">
                {fases.filter((f) => !above.has(f.id)).map((f) => (
                  <button key={f.id} type="button" className={"p-tog" + (fase === f.id ? " on" : "")} aria-pressed={fase === f.id} onClick={() => setFase(fase === f.id ? null : f.id)}>
                    <Icon name="arrow" size={13} /><span className="p-fase" style={{ marginRight: 2 }}>Fase</span>{f.title}
                    <span className="t-mute" style={{ fontWeight: 400 }}>· {deadline(f.end, data.today).text}</span>
                  </button>
                ))}
              </div>
              <p className="p-kd-note">Aan: de taak start pas als die fase klaar is, en staat zo op de tijdlijn.</p>
            </div>
          )}
        </div>

        <div className="p-drawer-f">
          <span className="t-mute" style={{ fontSize: 13 }}>{lv
            ? "Planner: " + klantName + " · Clockify: " + (plan ? "project " + plan.name : "nog niet gekoppeld")
            : "Planner: plan van de gekozen klant" + (chosen && !med ? " · eigenaar " + firstName(data.people.find((p) => p.id === chosen)?.name || "") : "")}</span>
          <span style={{ display: "flex", gap: 8 }}>
            <button type="button" className="ex-btn" onClick={close}>Annuleren</button>
            <button type="button" className="ex-btn ex-btn--primary" disabled={create.busy} onClick={submit}><Icon name="check" size={15} />Aanmaken</button>
          </span>
        </div>
      </aside>
    </>
  );
}
